import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge, Form } from "react-bootstrap";
import {
  fetchStockAdjustments,
  deleteStockAdjustment,
  clearError,
  clearSuccess,
} from "../../redux/slices/inventorySlice";
import { fetchProducts } from "../../redux/slices/productSlice";
import StockAdjustmentModal from "./StockAdjustmentModal";
import InfoPill from "../Common/InfoPill";
import { toast } from "react-toastify";

const StockAdjustments = () => {
  const dispatch = useDispatch();
  const {
    stockAdjustments = [],
    isLoadingStockAdjustments,
    error,
    success,
    isDeletingStockAdjustment,
  } = useSelector((state) => state.inventory);

  const { products = [] } = useSelector((state) => state.products);

  const [selectedStockAdjustments, setSelectedStockAdjustments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingStockAdjustment, setEditingStockAdjustment] = useState(null);
  const [productFilter, setProductFilter] = useState("");

  useEffect(() => {
    dispatch(fetchProducts());
    // Clear any existing success message when component mounts
    dispatch(clearSuccess());
  }, [dispatch]);

  useEffect(() => {
    const params = productFilter ? { product_id: productFilter } : {};
    dispatch(fetchStockAdjustments(params));
  }, [dispatch, productFilter]);

  // Show toast messages for success and error
  useEffect(() => {
    if (error) {
      toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  // Cleanup effect to clear success message when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  const handleCreateStockAdjustment = () => {
    setEditingStockAdjustment(null);
    setShowModal(true);
  };

  const handleEditStockAdjustment = (stockAdjustment) => {
    setEditingStockAdjustment(stockAdjustment);
    setShowModal(true);
  };

  const handleViewStockAdjustment = (stockAdjustment) => {
    setEditingStockAdjustment(stockAdjustment);
    setShowModal(true);
  };

  const handleDeleteStockAdjustment = (stockAdjustmentId) => {
    if (
      window.confirm("Are you sure you want to delete this stock adjustment?")
    ) {
      dispatch(deleteStockAdjustment(stockAdjustmentId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedStockAdjustments.length === 0) {
      alert("Please select stock adjustments to delete");
      return;
    }
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedStockAdjustments.length} stock adjustments?`
      )
    ) {
      selectedStockAdjustments.forEach((stockAdjustmentId) => {
        dispatch(deleteStockAdjustment(stockAdjustmentId));
      });
    }
  };

  const handleSelectStockAdjustment = (stockAdjustmentId) => {
    setSelectedStockAdjustments((prev) =>
      prev.includes(stockAdjustmentId)
        ? prev.filter((id) => id !== stockAdjustmentId)
        : [...prev, stockAdjustmentId]
    );
  };

  const handleSelectAll = () => {
    const safeStockAdjustments = Array.isArray(stockAdjustments)
      ? stockAdjustments
      : [];
    if (selectedStockAdjustments.length === safeStockAdjustments.length) {
      setSelectedStockAdjustments([]);
    } else {
      setSelectedStockAdjustments(safeStockAdjustments.map((sa) => sa.id));
    }
  };

  const getAdjustmentTypeBadge = (type) => {
    const typeConfig = {
      increase: { variant: "success", text: "Increase", icon: "↗" },
      decrease: { variant: "danger", text: "Decrease", icon: "↘" },
    };
    const config = typeConfig[type] || {
      variant: "secondary",
      text: type,
      icon: "?",
    };
    return (
      <Badge bg={config.variant}>
        {config.icon} {config.text}
      </Badge>
    );
  };

  const getReasonBadge = (reason) => {
    const reasonConfig = {
      damage: { variant: "danger", text: "Damage" },
      expired: { variant: "warning", text: "Expired" },
      manual: { variant: "info", text: "Manual" },
      return: { variant: "primary", text: "Return" },
      found: { variant: "success", text: "Found" },
      theft: { variant: "dark", text: "Theft" },
      correction: { variant: "secondary", text: "Correction" },
      purchase: { variant: "success", text: "Purchase" },
      purchase_cancellation: {
        variant: "warning",
        text: "Purchase Cancellation",
      },
    };
    const config = reasonConfig[reason] || {
      variant: "secondary",
      text: reason,
    };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    const { formatDateToDDMMYYYY } = require("../../utils/dateUtils");
    return formatDateToDDMMYYYY(dateString) || "-";
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p.id === productId);
    return product ? product.name : `Product #${productId}`;
  };

  const tableHeaders = [
    // {
    //   prop: "checkbox",
    //   title: (
    //     <input
    //       type="checkbox"
    //       checked={
    //         selectedStockAdjustments.length ===
    //           (Array.isArray(stockAdjustments) ? stockAdjustments.length : 0) &&
    //         (Array.isArray(stockAdjustments) ? stockAdjustments.length : 0) > 0
    //       }
    //       onChange={handleSelectAll}
    //     />
    //   ),
    //   cell: (row) => (
    //     <input
    //       type="checkbox"
    //       checked={selectedStockAdjustments.includes(row.id)}
    //       onChange={() => handleSelectStockAdjustment(row.id)}
    //     />
    //   ),
    // },
    {
      prop: "product_id",
      title: "Product",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <span className="fw-bold text-primary">
          {getProductName(row.product_id)}
        </span>
      ),
    },
    {
      prop: "adjustment_type",
      title: "Type",
      isSortable: true,
      cell: (row) => getAdjustmentTypeBadge(row.adjustment_type),
    },
    {
      prop: "quantity",
      title: "Quantity",
      isSortable: true,
      cell: (row) => (
        <Badge bg={row.adjustment_type === "increase" ? "success" : "danger"}>
          {row.adjustment_type === "increase" ? "+" : "-"}
          {row.quantity}
        </Badge>
      ),
    },
    {
      prop: "reason",
      title: "Reason",
      isSortable: true,
      cell: (row) => getReasonBadge(row.reason),
    },
    {
      prop: "date",
      title: "Date",
      isSortable: true,
      cell: (row) => formatDate(row.date),
    },
    {
      prop: "created_at",
      title: "Created",
      isSortable: true,
      cell: (row) => formatDate(row.created_at),
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleViewStockAdjustment(row)}
            disabled="true"
          >
            View
          </Button>
          <Button
            size="sm"
            variant="outline-warning"
            onClick={() => handleEditStockAdjustment(row)}
            disabled="true"
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDeleteStockAdjustment(row.id)}
            disabled="true"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  if (isLoadingStockAdjustments) {
    return <div className="text-center mt-4">Loading stock adjustments...</div>;
  }

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="page-container">
      {/* Breadcrumb */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Stock Adjustments
        </Link>
      </div>

      {/* Bulk Delete Button */}
      {/* {selectedStockAdjustments.length > 0 && (
        <div className="mb-3">
          <Button
            variant="danger"
            onClick={handleBulkDelete}
            disabled={isDeletingStockAdjustment}
            className="btn-sm"
          >
            Delete Selected ({selectedStockAdjustments.length})
          </Button>
        </div>
      )} */}

      <DatatableWrapper
        body={Array.isArray(stockAdjustments) ? stockAdjustments : []}
        headers={tableHeaders}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="d-flex align-items-left mb-2">
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            {/* Search Filter */}
            <div style={{ maxWidth: "300px" }}>
              <Filter
                placeholder="Search stock adjustments..."
                className="w-100 btn-success"
              />
            </div>

            {/* Product Filter */}
            <div style={{ maxWidth: "250px" }}>
              <Form.Select
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
              >
                <option value="">All Products</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          <div className="d-flex align-items-left gap-2">
            <PaginationOptions labels={labels} />
            <InfoPill
              left="Total Stock Adjustments"
              right={
                Array.isArray(stockAdjustments) ? stockAdjustments.length : 0
              }
            />
          </div>

          {/* <div className="d-flex align-items-center">
            <Button
              onClick={handleCreateStockAdjustment}
              variant="success"
              className="btn-sm btn-success"
              disabled={isLoadingStockAdjustments}
            >
              New Stock Adjustment
            </Button>
          </div> */}
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      {/* Stock Adjustment Modal */}
      <StockAdjustmentModal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setEditingStockAdjustment(null);
        }}
        stockAdjustment={editingStockAdjustment}
      />
    </div>
  );
};

export default StockAdjustments;
